import {
  Box,
  Button,
  ButtonGroup,
  Center,
  HStack,
  Image,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { parseISO } from "date-fns";
import FileSaver from "file-saver";
import React from "react";
import { useSelectDateGeneMap } from "../../routes/registries/stores/select/useSelectDateGeneMap";
import { useSelectTypeGeneMap } from "../../routes/registries/stores/select/useSelectTypeGeneMap";
import { getGeneMapOptions } from "../../routes/registries/util/geneChart";
import { useCreateVariantMap } from "../../services/api/charts/mutations/useCreateVariantMap";
import { LibBoxDNASpinner } from "../lib";
import { MaterialIcon } from "../shared/MaterialIcon";
import { Status } from "../shared/Status";

interface IProps {
  gridRef: any;
}

const GeneMap = ({ gridRef }: IProps) => {
  const [isOutOfSync, setIsOutOfSync] = React.useState(false);
  const [isErrorDisplay, setIsErrorDisplay] = React.useState(false);
  const [isAnyFilterPresent, setIsAnyFilterPresent] = React.useState(false);
  const [noRowsDisplay, setNoRowsDisplay] = React.useState(false);
  const [generatedVariantMapUrl, setGeneratedVariantMapUrl] =
    React.useState("");

  const dateGeneMap = useSelectDateGeneMap();
  const typeGeneMap = useSelectTypeGeneMap();
  const toast = useToast();

  const {
    mutateAsync: mutateAsyncVariantMap,
    isLoading: isLoadingVariantMap,
    isError: isErrorVariantMap,
  } = useCreateVariantMap();

  const handleOnRemoveFilters = () => {
    const gridApi = gridRef?.current?.api;

    if (gridApi) {
      gridApi.setFilterModel(null);
      toast({
        title: "Removed filters",
        description: ``,
        status: "info",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const handleOnDownload = async () => {
    FileSaver.saveAs(generatedVariantMapUrl, "gene-map.png");
    toast({
      title: "File downloaded",
      description: ``,
      status: "info",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleOnRefresh = () => {
    handleOnGenerate(dateGeneMap, typeGeneMap);
  };

  const handleOnGenerate = React.useCallback(
    async (geneDateValue, geneTypeValue) => {
      if (!geneTypeValue) {
        return;
      }

      setGeneratedVariantMapUrl("");
      setIsOutOfSync(false);
      setIsErrorDisplay(false);
      setNoRowsDisplay(false);
      setIsAnyFilterPresent(false);

      const gridApi = gridRef?.current?.api;

      if (gridApi) {
        const rowCount = gridApi.getDisplayedRowCount();
        const isAnyFilterPresentUpdate = gridApi.isAnyFilterPresent();

        setIsAnyFilterPresent(isAnyFilterPresentUpdate);

        let dateNewCase: Date | undefined;

        if (geneDateValue) {
          dateNewCase = parseISO(geneDateValue);
        }

        if (rowCount) {
          const geneChartInputs = getGeneMapOptions(
            gridApi,
            geneTypeValue,
            dateNewCase
          );

          if (geneChartInputs) {
            const url = await mutateAsyncVariantMap(geneChartInputs);
            setGeneratedVariantMapUrl(url);
          } else {
            setIsErrorDisplay(true);
          }
        } else {
          setNoRowsDisplay(true);
        }
      }
    },
    [gridRef, mutateAsyncVariantMap]
  );

  const onFilterChanged = React.useCallback(() => {
    setIsOutOfSync(true);
  }, []);

  React.useEffect(() => {
    const gridApi = gridRef?.current?.api;

    if (gridApi) {
      gridApi.addEventListener("filterChanged", onFilterChanged);
    }

    return () => {
      if (gridApi) {
        gridApi.removeEventListener("filterChanged", onFilterChanged);
      }
    };
  }, [onFilterChanged, gridRef]);

  React.useEffect(() => {
    setIsOutOfSync(true);
  }, [dateGeneMap, typeGeneMap]);

  React.useEffect(() => {
    handleOnGenerate(dateGeneMap, typeGeneMap);
    // eslint-disable-next-line
  }, []);

  return (
    <Stack h="full" px={6} pt={2} pb={8} flex={1} position="relative">
      <Box pos={"absolute"} top={4} right={4} left={4}>
        <HStack spacing={4} h="full" justify={"space-between"} w="full">
          <HStack px={2} py={4} spacing={2}>
            <Text
              whiteSpace={"nowrap"}
              textStyle="header"
              color="text.soft"
            >Gene: 
            </Text>
            <Text
              fontStyle={"italic"}
              whiteSpace={"nowrap"}
              textStyle="header"
              color="text.soft"
            >
              {typeGeneMap}
            </Text>
          </HStack>
          <ButtonGroup size="sm" variant={"outline"}>
            <Button
              isDisabled={!generatedVariantMapUrl}
              onClick={handleOnDownload}
              rightIcon={
                <MaterialIcon
                  name="file_download"
                  variant="outline"
                  fontSize="1rem"
                />
              }
            >
              Download
            </Button>
            <Button
              aria-label="Generate Variant Map"
              onClick={handleOnRefresh}
              isLoading={isLoadingVariantMap}
              loadingText="Generating..."
              isDisabled={isLoadingVariantMap}
              colorScheme={isOutOfSync ? "yellow" : "gray"}
              variant={isOutOfSync ? "solid" : "outline"}
              rightIcon={
                <MaterialIcon
                  name="refresh"
                  variant="outline"
                  fontSize="1rem"
                />
              }
            >
              Refresh
            </Button>
          </ButtonGroup>
        </HStack>
      </Box>

      {(isErrorVariantMap || isErrorDisplay) && (
        <Center h="full">
          <Status.IsError title="Failed to load data" />
        </Center>
      )}

      {noRowsDisplay && !generatedVariantMapUrl && (
        <Center h="full">
          <Stack
            spacing={6}
            px={12}
            py={8}
            border="1px"
            borderColor={"border"}
            rounded="md"
            shadow={"sm"}
          >
            <Text>No rows available to display graph</Text>
            {isAnyFilterPresent && (
              <Button onClick={handleOnRemoveFilters}>Remove filters</Button>
            )}
          </Stack>
        </Center>
      )}

      {isLoadingVariantMap && !noRowsDisplay ? (
        <Center h="full">
          <LibBoxDNASpinner />
        </Center>
      ) : (
        <>
          {!isErrorVariantMap && generatedVariantMapUrl && (
            <Box h="full">
              <Image
                objectFit={"contain"}
                w="full"
                h="full"
                src={generatedVariantMapUrl}
              ></Image>
            </Box>
          )}
        </>
      )}
    </Stack>
  );
};

export default GeneMap;
